import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ProjectTypeLabels } from '@bizsolutions/shared';

interface Account {
  id: string;
  name: string;
}

interface ProjectData {
  id: string;
  name: string;
  description: string | null;
  projectType: string;
  status: string;
  priority: string;
  billingType: string;
  budgetAmount: number | null;
  budgetHours: number | null;
  hourlyRate: number | null;
  startDate: string | null;
  targetEndDate: string | null;
  accountId: string | null;
  projectManagerId: string | null;
  notes: string | null;
}

export function ProjectForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [form, setForm] = useState({
    name: '',
    description: '',
    projectType: 'consulting',
    priority: 'medium',
    billingType: 'time_and_materials',
    budgetAmount: '',
    budgetHours: '',
    hourlyRate: '',
    startDate: '',
    targetEndDate: '',
    accountId: '',
    notes: '',
  });

  // Fetch accounts for dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-dropdown'],
    queryFn: async () => {
      const r = await api.getAccounts({ limit: 100 });
      return (r.data || []) as Account[];
    },
  });

  // Fetch existing project if editing
  const { data: projectData } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const r = await api.getProject(id!);
      return r.data as ProjectData;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (projectData && isEdit) {
      setForm({
        name: projectData.name || '',
        description: projectData.description || '',
        projectType: projectData.projectType || 'consulting',
        priority: projectData.priority || 'medium',
        billingType: projectData.billingType || 'time_and_materials',
        budgetAmount: projectData.budgetAmount?.toString() || '',
        budgetHours: projectData.budgetHours?.toString() || '',
        hourlyRate: projectData.hourlyRate?.toString() || '',
        startDate: projectData.startDate?.split('T')[0] || '',
        targetEndDate: projectData.targetEndDate?.split('T')[0] || '',
        accountId: projectData.accountId || '',
        notes: projectData.notes || '',
      });
    }
  }, [projectData, isEdit]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createProject(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-dashboard'] });
      toast({ title: 'Project created' });
      const newProject = result.data as { id: string };
      navigate(`/projects/list/${newProject?.id || ''}`);
    },
    onError: () => toast({ title: 'Failed to create project', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.updateProject(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      toast({ title: 'Project updated' });
      navigate(`/projects/list/${id}`);
    },
    onError: () => toast({ title: 'Failed to update project', variant: 'destructive' }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: Record<string, unknown> = {
      name: form.name,
      description: form.description || null,
      projectType: form.projectType,
      priority: form.priority,
      billingType: form.billingType,
      budgetAmount: form.budgetAmount ? parseFloat(form.budgetAmount) : null,
      budgetHours: form.budgetHours ? parseFloat(form.budgetHours) : null,
      hourlyRate: form.hourlyRate ? parseFloat(form.hourlyRate) : null,
      startDate: form.startDate || null,
      targetEndDate: form.targetEndDate || null,
      accountId: form.accountId || null,
      notes: form.notes || null,
    };

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">{isEdit ? 'Edit Project' : 'New Project'}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Type</Label>
                <Select value={form.projectType} onValueChange={(v) => setForm({ ...form, projectType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ProjectTypeLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client / Account</Label>
                <Select value={form.accountId} onValueChange={(v) => setForm({ ...form, accountId: v })}>
                  <SelectTrigger><SelectValue placeholder="Internal (no client)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Internal (no client)</SelectItem>
                    {(accountsData || []).map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing Type</Label>
                <Select value={form.billingType} onValueChange={(v) => setForm({ ...form, billingType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="time_and_materials">Time & Materials</SelectItem>
                    <SelectItem value="fixed_price">Fixed Price</SelectItem>
                    <SelectItem value="retainer">Retainer</SelectItem>
                    <SelectItem value="milestone">Milestone-based</SelectItem>
                    <SelectItem value="not_billable">Not Billable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Budget Amount ($)</Label>
                <Input type="number" step="0.01" value={form.budgetAmount} onChange={(e) => setForm({ ...form, budgetAmount: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <Label>Budget Hours</Label>
                <Input type="number" step="0.5" value={form.budgetHours} onChange={(e) => setForm({ ...form, budgetHours: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label>Hourly Rate ($)</Label>
                <Input type="number" step="0.01" value={form.hourlyRate} onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })} placeholder="0.00" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              </div>
              <div>
                <Label>Target End Date</Label>
                <Input type="date" value={form.targetEndDate} onChange={(e) => setForm({ ...form, targetEndDate: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" disabled={!form.name || createMutation.isPending || updateMutation.isPending}>
                {isEdit ? 'Save Changes' : 'Create Project'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
